import { Box, ValidationMessage } from '@modusign/design-system';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useMount } from 'react-use';

import { AddressBookContact, AddressBookSearchInput } from '@app/features/addressBook';
import { containsEmoji } from '@app/libs/validation';

function nameValidator(value: string) {
  const v = value?.trim();
  const rexExp = /[/?<>\\":*|₩]/g;

  if (v.length < 2) {
    return '2자 이상 입력해주세요.';
  }
  if (v.length > 30) {
    return '최대 입력가능한 길이는 30자입니다.';
  }
  if (rexExp.test(v)) {
    return '/?<>\\":*|₩은 입력할 수 없습니다.';
  }
  if (containsEmoji(v)) {
    return '이모지는 사용할 수 없습니다.';
  }
  return true;
}

interface SignerFormNameFieldProps {
  signerId: string;
  required?: boolean;
  onAddressBookContactSelect?: (contact: AddressBookContact) => void;
}

function SignerFormNameField({
  signerId,
  required = true,
  onAddressBookContactSelect,
}: SignerFormNameFieldProps) {
  const [isTouched, setIsTouched] = useState(false);

  const { control, errors, trigger } = useFormContext();
  const {
    field: { ref, value, onChange },
  } = useController({
    control,
    defaultValue: '',
    name: `${signerId}.name`,
    rules: {
      required: required ? '이름을 입력해주세요.' : undefined,
      validate: {
        isInvalid: (v) => {
          /**
           * 템플릿에서는 required를 props로 전달해줘서 이름이 필수가 아닐 수 있습니다.
           * 이 경우 유효성 검사를 통과하게 수정합니다.
           */
          if (!required && !v) {
            return true;
          }
          return nameValidator(v);
        },
      },
    },
  });

  const error = errors[signerId]?.name?.message;

  useMount(() => {
    if (ref.current) {
      ref.current.focus();
    }
  });

  function handleSetIsTouched() {
    if (!isTouched) {
      setIsTouched(true);
    }
  }

  function handleBlur() {
    handleSetIsTouched();
  }

  const debounceTrigger = debounce(() => {
    trigger();
  }, 600);

  function handleChange(changedValue: string) {
    handleSetIsTouched();
    onChange(changedValue);
    debounceTrigger();
  }

  return (
    <Box
      className={cx({
        InputSet: true,
        'InputSet--theme-editor': true,
        'InputSet--invalid': isTouched && error,
      })}
      mb={3}
    >
      <AddressBookSearchInput
        initialValue={value}
        isInvalid={!!error}
        placeholder="이름"
        onBlur={handleBlur}
        onChange={handleChange}
        onSelected={onAddressBookContactSelect}
      />
      {error && (
        <ValidationMessage fontSize="sm" fontWeight="regular">
          {error}
        </ValidationMessage>
      )}
    </Box>
  );
}

export default SignerFormNameField;
